import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { SxProps } from "@mui/system";
import { styled, Theme } from "@mui/material/styles";
import { Box, Card, MenuItem, Tabs, TabsProps, TextField, useMediaQuery } from "@mui/material";
import useResizeObserver from "use-resize-observer";
import { Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { OptionalRecordContextProvider, RaRecord, Tab, TabProps, useRecordContext } from "react-admin";
import { Column } from "./Column";
import { Row } from "./Row";
import { MenuTab, MenuTabElement } from "./MenuTab";
import { MenuTabItemElement } from "./MenuTabItem";
import { fullWidthTabsStyle } from "./backofficeTheme";
import { Tab2 } from "./Tab2";
import { useNavigate } from "react-router";

type TabElement = ReactElement<TabProps & { oldValue?: number | string }, typeof Tab>;

export interface _NewDesignTabbedShowLayoutProps {
  /** Displayed above tabs. */
  header?: ReactNode;
  /** Displayed on the right side of the tab headers. */
  additionalButtons?: ReactNode;
  /** Replaces the tab headers with a select on small viewports. */
  mobileSelectAriaLabel?: string;
  className?: string;
  record?: RaRecord;
  rootPath?: string;
  sx?: SxProps;
  value?: any;
  separateCards?: boolean;
  syncWithHash?: boolean;
  onTabChange?: (activeTab: ReactElement) => void;
  contentPadding?: string | number;
  tabsButtonsSpacing?: number;
}

export type NewDesignTabbedShowLayoutProps =
  | (_NewDesignTabbedShowLayoutProps & {
      syncWithLocation: true;
      tabs: Array<TabElement | MenuTabElement | false | undefined | null>;
    })
  | (_NewDesignTabbedShowLayoutProps & {
      syncWithLocation?: false;
      tabs: Array<TabElement | false | undefined | null>;
    });

/**
 * Copied and adapted from react-admin `TabbedShowLayout` component.
 */
export const NewDesignTabbedShowLayout = (props: NewDesignTabbedShowLayoutProps & { fullWidth?: boolean }) => {
  const {
    sx,
    header,
    additionalButtons,
    mobileSelectAriaLabel,
    tabs: tabs_,
    className,
    syncWithLocation = true,
    syncWithHash = false,
    value, // oxlint-disable-line no-unused-vars
    separateCards,
    fullWidth,
    onTabChange,
    contentPadding,
    tabsButtonsSpacing,
    ...rest
  } = props;
  const record = useRecordContext(props);
  const navigate = useNavigate();
  const { hash } = useLocation();
  const tabs = (Children.toArray(tabs_).filter(isValidElement) as Array<TabElement | MenuTabElement>).map((tab) => {
    if (isTabElement(tab)) {
      if (syncWithLocation) {
        return cloneElement(tab, {
          replace: true, // <-- this will be passed to the rendered Link component
          ...(fullWidth ? { sx: { flex: 1 } } : {}),
        } as any) as TabElement;
      } else {
        return cloneElement(tab, {
          syncWithLocation: false,
          ...(fullWidth ? { sx: { flex: 1 } } : {}),
        }) as TabElement;
      }
    } else {
      return tab;
    }
  });

  let newHash = "";

  function hashToTabValue(hash: string): number | "" {
    if (hash) {
      // Remove leading "#" ...
      hash = hash.substring(1);
      // Remove any additional key value pairs ...
      const i = hash.indexOf("&");
      if (i > 0) {
        hash = hash.substring(0, i);
      }
    }
    if (hash) {
      const i = tabs.findIndex((it) => it.props.value === hash);
      if (i > 0) {
        return i;
      } else {
        const i = tabs.findIndex((it) => it.props.oldValue === hash);
        if (i > 0) {
          // eslint-disable-next-line react-compiler/react-compiler
          newHash = `#${tabs[i].props.value}`;
          return i;
        }
      }
    }
    return "";
  }

  const [tabValue, setTabValue] = useState(() => (syncWithHash ? hashToTabValue(hash) : ""));

  useEffect(() => {
    if (newHash) {
      navigate(newHash, { replace: true });
    }
  }, []);

  useEffect(() => {
    if (syncWithHash) {
      setTabValue(hashToTabValue(hash));
    }
  }, [syncWithHash, hash]);

  const handleTabChange = (_event: React.SyntheticEvent, value: any): void => {
    if (!syncWithLocation) {
      setTabValue(value);
      const effectiveValue = value || 0;
      if (typeof effectiveValue === "number") {
        const tab = tabs[effectiveValue];
        if (syncWithHash) {
          const url = new URL(window.location.href);
          url.hash = (tab.props.value ?? "").toString();
          browserHistory.replace(url.toString());
        }
        onTabChange?.(tab);
      }
    }
  };

  if (!record) {
    return null;
  }

  const tabsAndButtons = (
    <Row justifyContent="space-between" spacing={tabsButtonsSpacing ?? undefined} sx={{ minWidth: 0 }}>
      <TabbedShowLayoutTabs
        mobileSelectAriaLabel={mobileSelectAriaLabel}
        onChange={handleTabChange}
        syncWithHash={syncWithHash}
        syncWithLocation={syncWithLocation}
        value={tabValue}
        sx={{
          ...(fullWidth ? fullWidthTabsStyle : {}),
          "& .MuiTabs-indicator": { display: "none" },
        }}
      >
        {tabs}
      </TabbedShowLayoutTabs>
      {additionalButtons}
    </Row>
  );

  let body = separateCards ? (
    <Card
      sx={{
        background: "transparent",
        margin: "-5px",
        padding: "5px" /* <-- ensure that box shadow of cards is not truncated. */,
        flex: 1,
      }}
    >
      {syncWithLocation ? (
        <Outlet />
      ) : (
        tabs.map((tab, i) => (tabValue || 0) === i && cloneElement(tab as TabElement, { context: "content" }))
      )}
    </Card>
  ) : (
    <Card sx={sx}>
      {syncWithLocation ? (
        <>
          {header || <Box height="20px" />}
          {tabsAndButtons}
          <div className={TabbedShowLayoutClasses.content}>
            <Outlet />
          </div>
        </>
      ) : (
        <>
          {header}
          {tabsAndButtons}
          <div className={TabbedShowLayoutClasses.content}>
            {tabs.map((tab, i) => (tabValue || 0) === i && cloneElement(tab as TabElement, { context: "content" }))}
          </div>
        </>
      )}
    </Card>
  );

  if (separateCards) {
    const headerCard = (
      <Card sx={sx}>
        {header || <Box height="20px" />}
        {tabsAndButtons}
      </Card>
    );
    body = (
      <Column>
        {headerCard}
        <Box height="20px" />
        {body}
      </Column>
    );
  }

  const tabElements = tabs.filter(isTabElement);
  const menuTabElements = tabs.filter(isMenuTabElement);

  return (
    <OptionalRecordContextProvider value={props.record}>
      <Root className={className} contentPadding={contentPadding} {...sanitizeRestProps(rest)}>
        {syncWithLocation ? (
          <Routes>
            <Route path="/*" element={body}>
              {tabElements.map((tab, index) => (
                <Route
                  key={`Tab ${index}`}
                  path={tab.props.path ?? ""}
                  element={cloneElement(tab, { context: "content" })}
                />
              ))}
              {menuTabElements
                .map((menuTab) =>
                  menuTab.props.children
                    .filter(isMenuTabItemElement)
                    .map((menuTabItem) => (
                      <Route
                        key={menuTabItem.props.path}
                        path={menuTabItem.props.path}
                        element={cloneElement(menuTabItem, { context: "content" })}
                      />
                    )),
                )
                .flat()}
            </Route>
          </Routes>
        ) : (
          body
        )}
      </Root>
    </OptionalRecordContextProvider>
  );
};

const PREFIX = "RaTabbedShowLayout";

export const TabbedShowLayoutClasses = {
  content: `${PREFIX}-content`,
};

const Root = styled("div", {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
  shouldForwardProp: (prop) => prop !== "contentPadding",
})<{ contentPadding?: string | number }>(({ theme, contentPadding }) => ({
  flex: 1,
  [`& .${TabbedShowLayoutClasses.content}`]: {
    padding: contentPadding !== undefined ? contentPadding : `${theme.spacing(1)} ${theme.spacing(2)}`,
  },
}));

// oxlint-disable-next-line no-unused-vars
const sanitizeRestProps = ({ record, resource, initialValues, staticContext, translate, tabs, ...rest }: any) => rest;

function isTabElement(tab: TabElement | MenuTabElement): tab is TabElement {
  return tab.type === Tab || tab.type === Tab2;
}

function isMenuTabElement(tab: TabElement | MenuTabElement): tab is MenuTabElement {
  return tab.type === MenuTab;
}

function isMenuTabItemElement(x: MenuTabItemElement | false): x is MenuTabItemElement {
  return !!x;
}

interface TabbedShowLayoutTabsProps extends TabsProps {
  children: Array<TabElement | MenuTabElement>;
  mobileSelectAriaLabel?: string;
  syncWithHash?: boolean;
  syncWithLocation?: boolean;
}

function TabbedShowLayoutTabs({
  children,
  mobileSelectAriaLabel,
  onChange,
  syncWithHash,
  syncWithLocation,
  value,
  ...rest
}: TabbedShowLayoutTabsProps) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const tabsRef = useRef<HTMLDivElement>(null);
  const [minimumTabsWidth, setMinimumTabsWidth] = useState(0);
  const { ref: containerRef, width: containerWidth } = useResizeObserver<HTMLDivElement>({ box: "border-box" });
  const path = params["*"] ?? "";
  const canUseSelect = !!mobileSelectAriaLabel && (!!syncWithLocation || !!syncWithHash);
  const isContainerTooNarrow = !!containerWidth && !!minimumTabsWidth && containerWidth < minimumTabsWidth;
  const useSelect = canUseSelect && (isMobile || isContainerTooNarrow);

  useLayoutEffect(() => {
    const tabsScroller = tabsRef.current?.querySelector<HTMLElement>(".MuiTabs-scroller");
    if (!tabsScroller) {
      return;
    }
    if (tabsScroller.scrollWidth <= tabsScroller.clientWidth) {
      setMinimumTabsWidth(0);
      return;
    }
    setMinimumTabsWidth(tabsScroller.scrollWidth);
  }, [children, containerWidth]);

  if (mobileSelectAriaLabel && useSelect) {
    const selectOptions = children.flatMap((tab, index) =>
      isTabElement(tab)
        ? [{ label: tab.props.label, value: syncWithLocation ? (tab.props.path ?? "") : index > 0 ? index : "" }]
        : tab.props.children
            .filter(isMenuTabItemElement)
            .map((menuTabItem) => ({ label: menuTabItem.props.label, value: menuTabItem.props.path })),
    );
    const selectedValue = syncWithLocation ? path : value;
    const selectedOption = selectOptions.find((option) => option.value === selectedValue) ?? selectOptions[0];

    return (
      <Box
        className="NewDesignTabbedShowLayout-select"
        ref={containerRef}
        sx={{ flex: "1 1 auto", mb: isMobile ? 0 : "6px", minWidth: 0 }}
      >
        <TextField
          select
          value={selectedOption?.value ?? ""}
          inputProps={{ "aria-label": mobileSelectAriaLabel }}
          SelectProps={{ displayEmpty: true, renderValue: () => selectedOption?.label }}
          onChange={(event) => {
            const nextOption = selectOptions.find((option) => `${option.value}` === `${event.target.value}`);
            const nextValue = nextOption?.value ?? "";
            if (!syncWithLocation) {
              onChange?.(event, nextValue);
              return;
            }
            const basePathname = path
              ? location.pathname.slice(0, -(path.length + 1))
              : location.pathname.replace(/\/$/, "");
            const nextPath = `${nextValue}`;
            navigate(
              {
                pathname: nextPath ? `${basePathname}/${nextPath}` : basePathname,
                search: location.search,
                hash: location.hash,
              },
              { replace: true },
            );
          }}
          size="small"
          fullWidth
          sx={{
            minWidth: 0,
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
              overflow: "hidden",
              "& .MuiOutlinedInput-notchedOutline": { borderRadius: "inherit" },
            },
            "& .MuiSelect-select": { backgroundColor: "transparent" },
          }}
        >
          {selectOptions.map((option) => {
            return (
              <MenuItem key={option.value || "default"} value={option.value}>
                {option.label}
              </MenuItem>
            );
          })}
        </TextField>
      </Box>
    );
  }

  if (syncWithLocation) {
    value = params["*"]; // <-- this is only correct for <Tab> elements
    // Use the index as value if path points to a <MenuTabItem> element ...
    Children.forEach(children, (child, index) => {
      if (child && isValidElement(child) && isMenuTabElement(child)) {
        for (const menuTabItem of child.props.children.filter(isMenuTabItemElement)) {
          if (menuTabItem.props.path === path) {
            value = index;
            return;
          }
        }
      }
    });
  }

  return (
    <Box ref={containerRef} sx={{ flex: "1 1 auto", minWidth: 0 }}>
      <Tabs ref={tabsRef} indicatorColor="primary" onChange={onChange} value={value} {...rest}>
        {Children.map(children, (tab, index) => {
          if (!tab || !isValidElement(tab)) return null;
          const value = syncWithLocation
            ? isTabElement(tab)
              ? (tab.props.path ?? "")
              : index
            : index > 0
              ? index
              : "";
          if (isTabElement(tab)) {
            return cloneElement(tab, {
              context: "header",
              value,
              syncWithLocation,
            });
          } else {
            return cloneElement(tab, { value });
          }
        })}
      </Tabs>
    </Box>
  );
}
